import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { FavoritesProvider } from './context/FavoritesContext';
import ErrorBoundary from './components/ErrorBoundary';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import Dashboard from './pages/admin/Dashboard';
import Categories from './pages/admin/Categories';
import Products from './pages/admin/Products';
import Orders from './pages/admin/Orders';
import Sidebar from './components/admin/Sidebar';
import Navbar from './components/admin/Navbar';
import PaymentSettings from './pages/admin/PaymentSettings';
import HomeManagement from './pages/admin/HomeManagement';
import HomeAlternativeManagement from './pages/admin/HomeAlternativeManagement';
import HomeAlternative2 from './pages/customer/HomeAlternative2';
import HomeAlternative2Management from './pages/admin/HomeAlternative2Management';
import Brands from './pages/admin/Brands';

// Customer Pages
import Shop from './pages/customer/Shop';
import ProductDetail from './pages/customer/ProductDetail';
import Cart from './pages/customer/Cart';
import Checkout from './pages/customer/Checkout';
import Login from './pages/customer/Login';
import Register from './pages/customer/Register';
import PaymentSuccess from './pages/customer/PaymentSuccess';
import PaymentFailed from './pages/customer/PaymentFailed';
import Loader from './components/common/Loader';
import Contact from './pages/customer/Contact';
import Home from './pages/customer/Home';
import HomeAlternative from './components/customer/HomeAlternative';
import CarWheelHomePage from './pages/customer/CarWheelHomePage';
import KidoLanding from './pages/customer/KidoLanding/KidoLanding';
import MyOrders from './pages/customer/MyOrders';
import Favorites from './pages/customer/Favorites';
import Terms from './pages/customer/TermsAndCondition';


import './styles/global.css';
import './styles/admin.css';
import './styles/components.css';
import './styles/customer.css';
import Header from './pages/customer/KidoLanding/components/Header';
import Footer from './pages/customer/KidoLanding/components/Footer';
import PrivacyPolicy from './pages/customer/PrivacyPolicy';
import ReturnPolicy from './pages/customer/ReturnPolicy';

// Protected Route for Admin
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) return <Loader fullscreen />;
  
  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/management/login" replace />;
  }

  return children;
};

// Protected Route for Customers (Cart & Checkout)
const CustomerProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <Loader fullscreen />;
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Admin Layout with Mobile Sidebar Support
const AdminLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className="admin-layout">
      <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
      <div className="admin-main">
        <Navbar 
          onToggleSidebar={toggleSidebar} 
          isSidebarOpen={isSidebarOpen} 
        />
        <div className="admin-content">
          {children}
        </div>
      </div>
    </div>
  );
};

// Main App Component
function AppContent() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Customer Routes with Header/Footer */}
        {/* KIDO landing page (has its own header/footer) */}
        <Route path="/" element={
          <>
          <Header />
          <KidoLanding />
          </>
        } 
          />

        {/* Previous landing page, kept reachable in case you still need it. Safe to delete. */}
        <Route path="/old-home" element={
          <>
            <Header />
            <CarWheelHomePage />
            <Footer />
          </>
        } />

        <Route path="/shop" element={
          <>
            <Header />
            <Shop />
            <Footer />
          </>
        } />

        <Route path="/product/:slug" element={
          <>
            <Header />
            <ProductDetail />
            <Footer />
          </>
        } />

        <Route path="/login" element={
          <>
            <Header />
            <Login />
            <Footer />
          </>
        } />

        <Route path="/terms" element={
          <>
            <Header />
            <Terms />
            <Footer />
          </>
        } />

        <Route path="/privacy" element={
          <>
            <Header />
            <PrivacyPolicy />
            <Footer />
          </>
        } />

        <Route path="/money-return" element={
          <>
            <Header />
            <ReturnPolicy />
            <Footer />
          </>
        } />

        <Route path="/register" element={
          <>
            <Header />
            <Register />
            <Footer />
          </>
        } />

        <Route path="/contact" element={
          <>
            <Header />
            <Contact />
            <Footer />
          </>
        } />

        <Route path="/payment-success" element={
          <>
            <Header />
            <PaymentSuccess />
            <Footer />
          </>
        } />

        <Route path="/payment-failed" element={
          <>
            <Header />
            <PaymentFailed />
            <Footer />
          </>
        } />

        <Route path="/home-alt-2" element={
          <>
            <Header />
            <HomeAlternative2 />
            <Footer />
          </>
        } />

        {/* Protected Customer Routes */}
        <Route path="/cart" element={
          <CustomerProtectedRoute>
            <Header />
            <Cart />
            <Footer />
          </CustomerProtectedRoute>
        } />

        <Route path="/checkout" element={
          <CustomerProtectedRoute>
            <Header />
            <Checkout />
            <Footer />
          </CustomerProtectedRoute>
        } />

        <Route path="/my-orders" element={
          <CustomerProtectedRoute>
           <Header />
            <MyOrders />
            <Footer />
          </CustomerProtectedRoute>
        } />
        <Route path="/favorites" element={
          <CustomerProtectedRoute>
            <Header />
            <Favorites />
            <Footer />
          </CustomerProtectedRoute>
        } />


        {/* Admin Routes (No Header/Footer) */}
        <Route path="/management/login" element={<AdminLogin />} />

        <Route path="/management/dashboard" element={
          <ProtectedRoute>
            <AdminLayout>
              <Dashboard />
            </AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/management/categories" element={
          <ProtectedRoute>
            <AdminLayout>
              <Categories />
            </AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/management/products" element={
          <ProtectedRoute>
            <AdminLayout>
              <Products />
            </AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/management/orders" element={
          <ProtectedRoute>
            <AdminLayout>
              <Orders />
            </AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/management/brands" element={
          <ProtectedRoute>
            <AdminLayout>
              <Brands />
            </AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/management/payment-settings" element={
          <ProtectedRoute>
            <AdminLayout>
              <PaymentSettings />
            </AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/management/home" element={
          <ProtectedRoute>
            <AdminLayout>
              <HomeAlternativeManagement />
            </AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/management/home-alternative" element={
          <ProtectedRoute>
            <AdminLayout>
              <HomeAlternativeManagement />
            </AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/management/home-alternative-2" element={
          <ProtectedRoute>
            <AdminLayout>
              <HomeAlternative2Management />
            </AdminLayout>
          </ProtectedRoute>
        } />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

// Root App with ErrorBoundary
function App() {
  // Debug info (remove after testing)
  console.log('=== APP DEBUG ===');
  console.log('API URL:', process.env.REACT_APP_API_URL);
  console.log('Node ENV:', process.env.NODE_ENV);
  console.log('=================');

  return (
    <ErrorBoundary>
      <AuthProvider>
        <FavoritesProvider>
          <CartProvider>
            <AppContent />
          </CartProvider>
        </FavoritesProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;